import { useEffect, useRef } from "react";

/**
 * Shtrix-kod skaneri (keyboard-wedge).
 *
 * Ombordagi skanerlar tizimga KLAVIATURA sifatida ulanadi: ular
 * kodni juda tez "yozib" beradi va oxirida Enter yuboradi.
 * Shuning uchun alohida drayver ham, IPC kanali ham kerak emas —
 * kerak bo'lgani tez ketma-ketlikni ODAM yozuvidan ajratish.
 *
 * dev_spek.md §10.3 talablari shu yerda bajariladi:
 *
 *  - debounce: belgilar orasidagi tanaffus chegaradan oshsa,
 *    yig'ilgan matn tashlanadi (bu odam yozuvi);
 *  - duplicate suppression: bir xil kod qisqa vaqt ichida ikki
 *    marta kelsa, ikkinchisi e'tiborga olinmaydi — skanerlar
 *    tugma bosilib turilganda takroriy o'qiydi.
 *
 * Maydonga yozayotgan odamga XALAQIT BERMAYDI: fokus `input`
 * yoki `textarea` da bo'lsa, kuzatuv umuman ishlamaydi.
 */

/** Skaner belgilar orasidagi eng katta tanaffus. */
const MAX_KEY_GAP_MS = 50;

/** Kod shu vaqt ichida takrorlansa, takror deb hisoblanadi. */
const DUPLICATE_WINDOW_MS = 1200;

/** Skaner kodining eng qisqa uzunligi — tasodifiy zarbalarni kesadi. */
const MIN_LENGTH = 4;

export function useBarcodeScanner(onScan: (code: string) => void, enabled = true): void {
  const handler = useRef(onScan);
  handler.current = onScan;

  useEffect(() => {
    if (!enabled) return;

    let buffer = "";
    let lastKeyAt = 0;
    let lastCode = "";
    let lastCodeAt = 0;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) {
        return;
      }

      const now = Date.now();
      if (now - lastKeyAt > MAX_KEY_GAP_MS) buffer = "";
      lastKeyAt = now;

      if (event.key === "Enter") {
        const code = buffer.trim();
        buffer = "";
        if (code.length < MIN_LENGTH) return;

        // Takroriy o'qish — skanerlar tugma bosilib turilganda
        // bir xil kodni ketma-ket yuboradi.
        if (code === lastCode && now - lastCodeAt < DUPLICATE_WINDOW_MS) return;
        lastCode = code;
        lastCodeAt = now;

        event.preventDefault();
        handler.current(code);
        return;
      }

      // Faqat bitta belgi beradigan tugmalar: Shift, Control va
      // boshqalar buferni buzmasligi kerak.
      if (event.key.length === 1) buffer += event.key;
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
